import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { toast } from "react-toastify";
// Constants
import { pathHomePage, WEB_SERVICE_MIMS, applications } from "../../../config/constants";
// Components
import { EmotionalQuestionAligner, EmotionalQuestionTitle, EmotionalQuestionContainer, NoAssignedQuestionsText } from "./styles";
import Axios from "../../../config/axios";
import Loader from "../../loader";
import PatientNavigation from "../../patientNavigation";
import SoftwareMenu from "../../softwareMenu";
// import QuestionnairesMenu from "../../questionnaires/questionnairesMenu";
import Title from "../../title";
import QuestionnaireInfo from "./sections/questionnaireInfo";
import ActionsManagement from "./sections/actionsManagement";
import EmotionalQuestion from "./sections/emotionalQuestion";

const EmotionalQuestions = () => {
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);
  const [isLoadingPatientQuestions, setIsLoadingPatientQuestions] = useState(true);
  const [loadingPatientQuestionsOrdering, setLoadingPatientQuestionsOrdering] = useState(true);
  const [isDisabledInputs, setIsDisabledInputs] = useState(true);

  const [emotionalQuestions, setEmotionalQuestions] = useState([]);
  const [patientEmotionalQuestions, setPatientEmotionalQuestions] = useState([]);
  const [iterablePatientEmotionalQuestions, setIterablePatientEmotionalQuestions] = useState([]);
  const [unassignedEmotionalQuestions, setUnassignedEmotionalQuestions] = useState([]);

  const { clinicianToken, patientId, software } = useParams();
  const [t] = useTranslation("global");

  // Fetch questions
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_MIMS}/emotional-questions/get-questions`)
      .then(({ data }) => {
        setEmotionalQuestions(data);
        setIsLoadingQuestions(false);
      })
      .catch((error) => {
        console.log("Endpoint to get emotional questions: ", error);
      });
  }, []);

  // Fetch patient questions
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_MIMS}/emotional-questions/get-patient-questions/${patientId}/${applications[software].id}`)
      .then(({ data }) => {
        setPatientEmotionalQuestions(data);
        setIterablePatientEmotionalQuestions(data);
        setIsLoadingPatientQuestions(false);
      })
      .catch((error) => {
        console.log("Endpoint to get patient emotional questions: ", error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, software]);

  // Set unassigned emotional questions
  useEffect(() => {
    const unassignedEmotionalQuestions = emotionalQuestions.filter(
      (emotionalQuestion) =>
        !iterablePatientEmotionalQuestions.some(
          (iterablePatientEmotionalQuestion) =>
            emotionalQuestion.EMOTIONAL_QUESTION_ID === iterablePatientEmotionalQuestion.EMOTIONAL_QUESTION_ID
        )
    );
    setUnassignedEmotionalQuestions(unassignedEmotionalQuestions);
    setLoadingPatientQuestionsOrdering(false);
  }, [emotionalQuestions, iterablePatientEmotionalQuestions]);

  const setInitialState = () => {
    setIterablePatientEmotionalQuestions(patientEmotionalQuestions);
    setIsDisabledInputs(true);
  };

  const savePatientEmotionalQuestions = () => {
    Axios(
      "post",
      `${WEB_SERVICE_MIMS}/emotional-questions/add-patient-questions/${patientId}/${applications[software].id}`,
      iterablePatientEmotionalQuestions
    )
      .then(({ data }) => {
        if (data.patientEmotionalQuestionsAdded) {
          setIsDisabledInputs(true);
          toast.success(t("questionnaires_alerts_addedQuestions"));
        }
      })
      .catch((error) => {
        console.log("Endpoint to store patient emotional questions: ", error);
      });
  };

  const renderEmotionalQuestions = () => {
    const handleDragEnd = (event) => {
      const { activatorEvent, active, over } = event;

      const assignedQuestionsContainer = document.getElementById("assignedQuestions");
      const unassignedQuestionsContainer = document.getElementById("unassignedQuestions");
      const element = activatorEvent.target;
      const assignedQuestionsContainerPosition = assignedQuestionsContainer.getBoundingClientRect();
      const unassignedQuestionsContainerPosition = unassignedQuestionsContainer.getBoundingClientRect();
      const currentQuestion = element.getBoundingClientRect();

      const isTouchingAssignedQuestions = () => {
        return (
          currentQuestion.top >= assignedQuestionsContainerPosition.top &&
          currentQuestion.bottom <= assignedQuestionsContainerPosition.bottom &&
          currentQuestion.left >= assignedQuestionsContainerPosition.left &&
          currentQuestion.right <= assignedQuestionsContainerPosition.right
        );
      };

      const isTouchingUnassignedQuestions = () => {
        return (
          currentQuestion.top >= unassignedQuestionsContainerPosition.top &&
          currentQuestion.bottom <= unassignedQuestionsContainerPosition.bottom &&
          currentQuestion.left >= unassignedQuestionsContainerPosition.left &&
          currentQuestion.right <= unassignedQuestionsContainerPosition.right
        );
      };

      const oldQuestionIndex = iterablePatientEmotionalQuestions.findIndex((question) => question.EMOTIONAL_QUESTION_ID === active.id);
      const newQuestionIndex = iterablePatientEmotionalQuestions.findIndex((question) => question.EMOTIONAL_QUESTION_ID === over.id);
      const newUnassignedQuestionIndex = unassignedEmotionalQuestions.findIndex((question) => question.EMOTIONAL_QUESTION_ID === over.id);

      // Move a question position in the Patient questions
      if (oldQuestionIndex >= 0 && newQuestionIndex >= 0 && isTouchingAssignedQuestions()) {
        return setIterablePatientEmotionalQuestions(arrayMove(iterablePatientEmotionalQuestions, oldQuestionIndex, newQuestionIndex));
      }
      // Add a question to the Patient questions
      if (
        (oldQuestionIndex < 0 && newQuestionIndex < 0 && isTouchingAssignedQuestions()) ||
        (oldQuestionIndex < 0 && newQuestionIndex >= 0 && isTouchingAssignedQuestions())
      ) {
        if (iterablePatientEmotionalQuestions.length === 3) return toast.error(t("questionnaires_alerts_noMoreThanThreeQuestions"));
        const questionToAdd = unassignedEmotionalQuestions.find((question) => question.EMOTIONAL_QUESTION_ID === active.id);
        const newIterablePatientEmotionalQuestions = [...iterablePatientEmotionalQuestions, questionToAdd];
        return setIterablePatientEmotionalQuestions(newIterablePatientEmotionalQuestions);
      }
      // Remove a question from Patient questions
      if (
        (oldQuestionIndex >= 0 && newUnassignedQuestionIndex <= 0 && isTouchingUnassignedQuestions()) ||
        (oldQuestionIndex >= 0 && newUnassignedQuestionIndex >= 0 && isTouchingUnassignedQuestions())
      ) {
        const questionToRemove = iterablePatientEmotionalQuestions[oldQuestionIndex];
        const newIterablePatientEmotionalQuestions = iterablePatientEmotionalQuestions.filter(
          (patientEmotionalQuestion) => patientEmotionalQuestion.EMOTIONAL_QUESTION_ID !== questionToRemove.EMOTIONAL_QUESTION_ID
        );
        return setIterablePatientEmotionalQuestions(newIterablePatientEmotionalQuestions);
      }
    };

    return (
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        {/* Patient emotional questions */}
        <EmotionalQuestionAligner>
          <EmotionalQuestionTitle>{t("questionnaires_assignedQuestionsTitle")}</EmotionalQuestionTitle>
          <EmotionalQuestionContainer id="assignedQuestions">
            <SortableContext items={iterablePatientEmotionalQuestions} strategy={verticalListSortingStrategy} disabled={isDisabledInputs}>
              {!iterablePatientEmotionalQuestions.length && (
                <NoAssignedQuestionsText>{t("questionnaires_assignedQuestions")}</NoAssignedQuestionsText>
              )}
              {iterablePatientEmotionalQuestions.map((emotionalQuestion) => {
                return (
                  <EmotionalQuestion
                    key={emotionalQuestion.EMOTIONAL_QUESTION_ID}
                    emotionalQuestion={emotionalQuestion}
                    checkBackgroundColor={isDisabledInputs ? "#808080" : "#39b14a"}
                    isDisabledInputs={isDisabledInputs}
                    isPatientEmotionalQuestion
                  />
                );
              })}
            </SortableContext>
          </EmotionalQuestionContainer>
        </EmotionalQuestionAligner>
        {/* Unassigned emotional questions */}
        <EmotionalQuestionAligner>
          <EmotionalQuestionTitle>{t("questionnaires_unassignedQuestionsTitle")}</EmotionalQuestionTitle>
          <EmotionalQuestionContainer id="unassignedQuestions">
            <SortableContext items={unassignedEmotionalQuestions} strategy={verticalListSortingStrategy} disabled={isDisabledInputs}>
              {!unassignedEmotionalQuestions.length && (
                <NoAssignedQuestionsText>{t("questionnaires_unassignedQuestions")}</NoAssignedQuestionsText>
              )}
              {unassignedEmotionalQuestions.map((emotionalQuestion) => {
                return (
                  <EmotionalQuestion
                    key={emotionalQuestion.EMOTIONAL_QUESTION_ID}
                    emotionalQuestion={emotionalQuestion}
                    checkBackgroundColor="#fff"
                    isDisabledInputs={isDisabledInputs}
                  />
                );
              })}
            </SortableContext>
          </EmotionalQuestionContainer>
        </EmotionalQuestionAligner>
      </DndContext>
    );
  };

  return (
    <>
      {isLoadingQuestions || isLoadingPatientQuestions || loadingPatientQuestionsOrdering ? (
        <Loader />
      ) : (
        <>
          <PatientNavigation patientId={patientId} section={software} />
          <SoftwareMenu
            prescriptionsPath={`${pathHomePage}/prescriptions/app/${clinicianToken}/${patientId}`}
            diagnosisPrescriptionsPath={`${pathHomePage}/diagnosis-prescriptions/${clinicianToken}/${patientId}/${software}`}
            questionnairePath={`${pathHomePage}/emotional-questions/${clinicianToken}/${patientId}/${software}`}
            messagesPath={`${pathHomePage}/messages/${clinicianToken}/${patientId}/${software}`}
          />
          {/* <QuestionnairesMenu /> */}
          <Title title={`RGS ${software}`} subtitle={`/ ${t("questionnaires_questionnaire")}`} marginBottom="10px" />
          <QuestionnaireInfo />
          <ActionsManagement
            handleSubmit={savePatientEmotionalQuestions}
            isDisabledInputs={isDisabledInputs}
            setIsDisabledInputs={setIsDisabledInputs}
            setInitialState={setInitialState}
          />
          {renderEmotionalQuestions()}
        </>
      )}
    </>
  );
};

export default EmotionalQuestions;
